import React from 'react';
import { POPULAR_BRANDS } from '../config/siteConfig';
import * as BrandSVGs from './BrandLogos';

interface BrandLogoProps {
    brandName: string;
    className?: string;
    useSVG?: boolean; // Si true, usa los SVG personalizados de BrandLogos.tsx
    noFilter?: boolean; // Si true, muestra el logo a color sin filtros (para BrandCatalog)
}

/**
 * Componente para renderizar logos de marcas.
 * 
 * Soporta dos modos:
 * 1. useSVG=false (default): Usa <img> con rutas locales de public/brand-logos/
 * 2. useSVG=true: Usa componentes SVG personalizados de BrandLogos.tsx
 */
const BrandLogo: React.FC<BrandLogoProps> = ({ brandName, className = '', useSVG = false, noFilter = false }) => {
    const brandInfo = POPULAR_BRANDS.find(b => b.name === brandName);

    if (!brandInfo) {
        // Fallback genérico si no se encuentra la marca
        return (
            <div className={`flex items-center justify-center bg-zinc-800/50 rounded-full ${className}`}>
                <div className="text-zinc-500 scale-75">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>
                </div>
            </div>
        );
    }

    // Casos especiales: Apple, Samsung (Iconos) y Accesorios (Logo CCSV)
    if (['Apple', 'Samsung', 'Accesorios'].includes(brandName)) {
        if (brandName === 'Accesorios') {
            return (
                <img
                    src="/CCSV_LOGO.svg"
                    alt="Accesorios Logo"
                    className={`object-contain ${className}`}
                />
            );
        }

        // Para Apple y Samsung, usamos los componentes SVG (que ahora usan react-icons)
        const SVGComponent = BrandSVGs[`${brandName}Logo` as keyof typeof BrandSVGs] as React.FC<React.SVGProps<SVGSVGElement>>;

        if (SVGComponent) {
            // Si noFilter está activo (ej: cabecera de catálogo), mostramos el icono sin efectos de opacidad/filtro
            const effectsClass = noFilter
                ? ""
                : "filter brightness-0 invert opacity-60 group-hover:filter-none group-hover:opacity-100 transition-all duration-300";

            return (
                <SVGComponent
                    className={`${className} ${effectsClass}`}
                />
            );
        }
    }

    // Si noFilter=true, mostrar logo original sin efectos (para BrandCatalog)
    if (noFilter) {
        return (
            <img
                src={brandInfo.logo}
                alt={`${brandName} logo`}
                className={`object-contain ${className}`}
            />
        );
    }

    // Modo SVG personalizado
    if (useSVG) {
        const SVGComponent = BrandSVGs[`${brandName}Logo` as keyof typeof BrandSVGs] as React.FC<React.SVGProps<SVGSVGElement>>;

        if (SVGComponent) {
            return (
                <SVGComponent
                    className={`transition-all duration-300 filter brightness-0 invert opacity-60 group-hover:filter-none group-hover:opacity-100 ${className}`}
                />
            );
        }
    }

    // Modo imagen local (default) para marcas normales
    return (
        <img
            src={brandInfo.logo}
            alt={`${brandName} logo`}
            className={`object-contain transition-all duration-300 filter brightness-0 invert opacity-60 group-hover:filter-none group-hover:opacity-100 ${className}`}
            onError={(e) => {
                // Fallback si la imagen no carga
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                    parent.innerHTML = `<span class="text-zinc-600 text-xs font-bold uppercase">${brandName}</span>`;
                }
            }}
        />
    );
};

export default BrandLogo;
